import { isPlainObject } from 'lodash-es';

import { escapeRegExp } from './regex';

/**
 * Gets search match score of a datum on a query
 * @param datum the datum to be searched
 * @param query the query string
 * @param getSearchableString a function that transforms the datum to a string that can be searched
 * @returns an index representing how closely the datum matches the query. 0 is the strongest match score,
 * higher values represent weaker matches. -1 means no match.
 */
const getDatumIndex = <T>(
  datum: T,
  query: string,
  getSearchableString: (el: T) => string
): number => {
  const searchableDatum = getSearchableString(datum);
  let index = -1;

  const escapedQuery = escapeRegExp(query);
  const initialCharacterMatch = new RegExp(`^${escapedQuery}`, 'ui');
  const anyMatch = new RegExp(escapedQuery, 'ui');

  // search on entire datum
  if (query === searchableDatum) {
    return 0;
  } else if (initialCharacterMatch.test(searchableDatum)) {
    index = 1;
  } else if (anyMatch.test(searchableDatum)) {
    index = 2;
  }

  // search by word within datum
  const words = searchableDatum.split(' ');
  for (const [i, word_] of words.entries()) {
    const word = word_;
    if (query === word) {
      index = i;
      break;
    } else if (initialCharacterMatch.test(word)) {
      index = i + 1;
      break;
    } else if (anyMatch.test(word)) {
      index = i + 2;
    }
  }

  return index;
};

/**
 * Searches a query string on a specified component of data and returns matches.
 * @param data data to search
 * @param getSearchableString a function that maps a datum to a string that can be searched via string query
 * @param query the query string
 * @param reduce if true, does not include non matches in results. if false, includes non matches at the end of the results
 * @returns data ordered by closest match with query
 */
export const searchSort = <T>(
  data: T[],
  getSearchableString: (el: T) => string,
  query: string,
  reduce: boolean
) => {
  const results: Record<string, T[]> = {};

  for (const datum of data) {
    const index = getDatumIndex(datum, query, getSearchableString);
    const result = results[index] ?? [];
    result.push(datum);
    results[index] = result;
  }

  const finalResults: T[] = [];
  if (reduce) {
    for (const key of Object.keys(results)) {
      if (Number.parseInt(key, 10) !== -1) {
        const sorted = results[key] ?? [];
        finalResults.push(...sorted);
      }
    }
  } else {
    for (const key of Object.keys(results)) {
      const sorted = results[key] ?? [];
      finalResults.push(...sorted);
    }
  }

  return finalResults;
};

interface Match<T> {
  /*
   * match is a 3-element array where match[1] represents the matched part of the string
   * match[0] and match[2] are the substrings preceding and proceeding match[1] respectively.
   * for example:
   * - query: "app"
   * - datum string: "happy birthday"
   * - match: ["h", "app", "y birthday"]
   */
  match: [string, string, string];
  datum: T;
}

const sortArr = <T>(
  arr: Match<T>[],
  getSearchableString: (el: T) => string
) => {
  arr.sort((a, b) => {
    if (
      getSearchableString(a.datum)
        .toLowerCase()
        .indexOf(a.match[1].toLowerCase()) <
      getSearchableString(b.datum)
        .toLowerCase()
        .indexOf(b.match[1].toLowerCase())
    ) {
      return -1;
    }

    return 1;
  });
};

/**
 * Breaks each datum into chunks by query match. Chunks are string arrays of length 3 where the middle element is the matched chunk (equivalent to the query),
 * and the beginning and end are the substrings of the datum before and after the matched chunk, respectively.
 * Sorts the data by the index of the match.
 * @example
 * applySearchHighlight(["abc123"], (element) => element, "c1")
 * // returns [{ match: ["ab", "c1", "23"], datum: "abc123"}]
 *
 * @param data the data to search on
 * @param getSearchableString a function that maps a datum to a string that can be searched via string query
 * @param query the query string
 * @returns an array of each datum's chunks alongside the original datum
 */
export const applySearchHighlight = <T>(
  data: T[],
  getSearchableString: (el: T) => string,
  query: string
): {
  match: [string, string, string] | undefined;
  datum: T;
}[] => {
  if (!query) {
    return data.map((datum: T) => ({ match: undefined, datum }));
  }
  const matches = [];
  const noMatches = [];

  const escapedQuery = escapeRegExp(query);

  for (const datum of data) {
    const searchableDatum = getSearchableString(datum);
    const match = new RegExp(escapedQuery, 'iu').exec(searchableDatum);

    if (match?.index === undefined) {
      noMatches.push({
        match: undefined,
        datum,
      });
    } else {
      const begin = searchableDatum.slice(0, match.index);
      const middle = searchableDatum.slice(
        match.index,
        match.index + escapedQuery.length
      );
      const end = searchableDatum.slice(match.index + escapedQuery.length);
      matches.push({
        match: [begin, middle, end] as [string, string, string],
        datum,
      });
    }
  }
  sortArr(matches, getSearchableString);

  return [...matches, ...noMatches];
};

/**
 * Basic alphanumeric compare function
 * @param a reference string
 * @param b comparison string
 * @returns an integer based on the determined sort order
 */
export const compareAlphaNum = (a: string, b: string) =>
  a.localeCompare(b, 'en', { numeric: true });

type SortObject = Record<string, unknown>;

/**
 * Creates a custom compare function to be used with Array.prototype.sort.
 *
 * @example
 * ```ts
 * const compareFn = createCustomSortOrderCompareFn(['c', 'b', 'a'])
 *
 * ['f', 'a', 'b', 'c', 'd'].sort(compareFn)
 * // => ['f', 'c', 'b', 'a', 'd']
 * ```
 *
 * The items in the order param will be pushed to the front of the array
 * when sorting takes place.
 *
 * If an item to be sorted is not in the "order" array, it will be left in place.
 *
 * @param order - An array of strings providing a sort order.
 * @returns A sort callback compare function.
 */
export const createCustomSortOrderCompareFn =
  (order: string[]) => (a: string, b: string) => {
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    } else if (indexA !== -1) {
      return -1;
    } else if (indexB !== -1) {
      return 1;
    }

    return 0;
  };

/**
 * Sorts the keys of a given object in alphanumeric order.
 *
 * This function takes an object with unordered keys and returns a new object
 * with the same keys sorted in alphanumeric order. If the object contains nested
 * objects or arrays with objects, it recursively sorts their keys as well.
 *
 * Note: The sorting is case-sensitive and treats numbers as part of the strings.
 *
 * @param {T} unorderedObj - The object with unordered keys to be sorted.
 * @param compareFn - A optional custom compare function for sorting, which will override the default alphanumeric sort function.
 *
 * @returns {T} A new object with keys sorted in alphanumeric order.
 */
export const sortObjectKeys = <T extends SortObject>(
  unorderedObj: T,
  compareFn?: (a: string, b: string) => number
): T => {
  const sortedObj: SortObject = {};

  for (const key of Object.keys(unorderedObj).sort(
    compareFn ?? compareAlphaNum
  )) {
    const value = unorderedObj[key];
    if (Array.isArray(value)) {
      sortedObj[key] = value.map((element) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        isPlainObject(element) ? sortObjectKeys(element, compareFn) : element
      );
      continue;
    }

    sortedObj[key] = isPlainObject(value)
      ? sortObjectKeys(value as SortObject, compareFn)
      : value;
  }

  return sortedObj as T;
};

/**
 * Sorts an object's keys recursively and stringifies it.
 *
 * @param {SortObject} obj - the object to sort.
 * @returns {string} the sorted JSON text.
 */
const sortJsonObject = (obj: SortObject): string =>
  JSON.stringify(sortObjectKeys(obj), undefined, 2);

/**
 * Parses a JSON string, sorts its keys recursively, and re-stringifies it.
 * Falls back to the original text unchanged only if it isn't valid JSON at
 * all (e.g. an in-progress edit), so callers can diff/display it without
 * crashing.
 *
 * @param {string} jsonText - the JSON text to sort.
 * @returns {string} the sorted (or reformatted) JSON text, or the original
 * text if parsing failed.
 */
export const sortJsonText = (jsonText: string): string => {
  try {
    const parsed: unknown = JSON.parse(jsonText);
    if (!isPlainObject(parsed)) {
      return JSON.stringify(parsed, undefined, 2);
    }
    return sortJsonObject(parsed as SortObject);
  } catch {
    return jsonText;
  }
};

const compareCanonicalText = (a: string, b: string): number => {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
};

// compareAlphaNum alone is not canonical: it treats "a1" and "a01" as equal, which would
// leave their order input-dependent. Break ties by codepoint.
const compareKeysCanonically = (a: string, b: string): number =>
  compareAlphaNum(a, b) || compareCanonicalText(a, b);

const deepSortValue = (value: unknown, sortArrays: boolean): unknown => {
  if (Array.isArray(value)) {
    const elements = value.map((el) => deepSortValue(el, sortArrays));
    // Only arrays of objects sort — their position is layout (mods, components). Arrays of
    // primitives or arrays keep position: order there is data (coordinates, matrices).
    if (
      sortArrays &&
      elements.length > 1 &&
      elements.every((el) => isPlainObject(el))
    ) {
      return elements
        .map((el) => ({ el, key: JSON.stringify(el) }))
        .sort((a, b) => compareCanonicalText(a.key, b.key))
        .map(({ el }) => el);
    }
    return elements;
  }
  if (isPlainObject(value)) {
    const record = value as SortObject;
    // Null prototype so a literal "__proto__" key copies as a key instead of hitting the
    // prototype setter and vanishing. JSON.stringify treats it like a plain object.
    const sorted: SortObject = Object.create(null) as SortObject;
    for (const key of Object.keys(record).sort(compareKeysCanonically)) {
      sorted[key] = deepSortValue(record[key], sortArrays);
    }
    return sorted;
  }
  return value;
};

/**
 * Like sortJsonText, but also sorts arrays of objects (not primitives — their order is data).
 * Discards object-array order, so use for comparison/display only, never to write a config.
 * Pass sortArrays: false to keep every array in its stored order and sort keys only.
 *
 * @param json - the object or JSON text to sort.
 * @param sortArrays - whether arrays of objects are sorted; defaults to true.
 * @returns {string} the canonically sorted JSON text.
 */
export const deepSortJson = (
  json: SortObject | string,
  sortArrays = true
): string => {
  if (typeof json !== 'string') {
    return JSON.stringify(deepSortValue(json, sortArrays), undefined, 2);
  }
  try {
    return JSON.stringify(
      deepSortValue(JSON.parse(json), sortArrays),
      undefined,
      2
    );
  } catch {
    return json;
  }
};

/**
 * Basic compare function for sorting file names
 *
 * Note: This function does not expect file paths to be included.
 *
 * @example
 * compareFileNames('file.js', 'file.py')
 * // returns -1
 *
 * @param a reference file name
 * @param b comparison file name
 * @returns an integer based on the determined sort order
 */
export const compareFileNames = (a: string, b: string): number => {
  const aSegments = a.split('.');
  const bSegments = b.split('.');

  let comparisonResult = 0;
  let i = 0;

  for (const aSegment of aSegments) {
    comparisonResult = compareAlphaNum(aSegment, bSegments[i] ?? '');

    if (comparisonResult !== 0) {
      break;
    }

    i += 1;
  }

  return comparisonResult;
};
